import React from 'react'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import { NotesIcon } from '../../../components/Icons/Icons'
import TableSkeletonRows from '../../../components/Skeleton/TableSkeletonRows'
import { formatMoney } from '../../../utils/amount'
import { formatBusinessDate } from '../../../utils/date'
import { getStatusColorKey, formatReviewReason } from './helpers'

const CHIP_COLOR = { success: 'success', warning: 'warning', error: 'error', info: 'info', default: 'default' }

// processedOn (submit) / settledOn (settlement) are business facts — render them in business
// time (ET), never the viewer's browser timezone, so a business-midnight date never slips a day.
const formatMaybeDate = (value) => (value ? formatBusinessDate(value) : '—')

// "Settled On": the actual settle date once settled; otherwise the forward ETA (an estimate),
// clearly marked so it is never mistaken for a real settlement. A parked row carries no ETA.
const formatSettledOn = (settledOn, estimatedSettlementDate) => {
  if (settledOn) return formatBusinessDate(settledOn)
  if (estimatedSettlementDate) return `ETA ${formatBusinessDate(estimatedSettlementDate)}`
  return '—'
}

const MethodCell = ({ method }) => {
  if (!method) return <span className="text-sm text-text-secondary">—</span>
  const label = method.type === 'Card' ? method.brand || 'Card' : 'Bank'
  return (
    <div>
      <p className="text-sm text-text-primary">{label} •••• {method.last4 || '----'}</p>
      <p className="text-xs text-text-secondary">{method.type}</p>
    </div>
  )
}

const PaymentsHistoryTable = ({ rows = [], loading = false, totalRows, onRowClick, onNextAction, showReviewDetail = false }) => {
  // Skeleton rows only cover the very first load (no rows yet); once any rows
  // are present we keep showing them while a background refresh is in flight.
  const showSkeleton = loading && rows.length === 0

  return (
    <div className="rounded-2xl border border-divider bg-paper overflow-hidden">
      <div className="overflow-x-auto">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Payment &amp; Method</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Processor</TableCell>
              <TableCell>Transaction ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Details/Code</TableCell>
              <TableCell>Processed On</TableCell>
              <TableCell>Settled On</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Next Action</TableCell>
              <TableCell align="center">Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton && <TableSkeletonRows colSpan={13} />}
            {!showSkeleton && rows.map((row) => {
              const statusColorKey = getStatusColorKey(row.status)
              return (
                <TableRow
                  key={row.paymentId}
                  hover
                  onClick={() => onRowClick(row)}
                  className="cursor-pointer"
                  data-testid={`history-row-${row.paymentId}`}
                >
                  <TableCell>
                    <p className="text-sm font-medium text-text-primary">{row.clientName}</p>
                    <p className="text-xs text-text-secondary">{row.clientCode}</p>
                    {/* A verification only reaches this console when it is parked, i.e. a real $1-2
                        charge that may not have come back. Without the label an operator reads it
                        as a client payment and cannot tell why the amount makes no sense. */}
                    {row.isVerificationPayment && (
                      <span
                        className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-warning/15 text-warning"
                        data-testid={`verification-badge-${row.paymentId}`}
                      >
                        Card verification
                      </span>
                    )}
                    {row.isVerificationPayment && row.cardVerification && (
                      <p
                        className="text-[10px] text-text-secondary break-all"
                        data-testid={`verification-id-${row.paymentId}`}
                      >
                        {row.cardVerification}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <MethodCell method={row.method} />
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium text-text-primary">{formatMoney(row.amount)}</span>
                  </TableCell>
                  <TableCell>{row.method?.type || '—'}</TableCell>
                  <TableCell>{row.processor || '—'}</TableCell>
                  <TableCell data-testid={`transaction-id-${row.paymentId}`}>
                    {/* Legitimately absent for declines-at-submit / parked technical faults
                        (they never received a gateway id) — render a dash, never hide the row. */}
                    <span className="text-xs text-text-secondary break-all">{row.transactionId || '—'}</span>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.statusLabel || row.status}
                      color={CHIP_COLOR[statusColorKey]}
                      variant={statusColorKey === 'default' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell>
                    {/* Terminal rows carry a concrete auth/decline code in statusDetail; in-flight
                        rows PARKed for review have none, so fall back to a plain-language reviewReason
                        instead of a bare dash. */}
                    <span className="text-xs text-text-secondary">
                      {row.statusDetail || (showReviewDetail && row.reviewDetail) || formatReviewReason(row.reviewReason) || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-text-primary">{formatMaybeDate(row.processedOn)}</span>
                  </TableCell>
                  <TableCell data-testid={`settled-on-${row.paymentId}`}>
                    {/* Actual settlement instant once settled; otherwise a forward ETA marked
                        "ETA" so it is never mistaken for a real settlement. Parked rows and
                        pre-backfill legacy rows carry neither — a dash. */}
                    <span className="text-sm text-text-primary">{formatSettledOn(row.settledOn, row.estimatedSettlementDate)}</span>
                  </TableCell>
                  <TableCell>{row.ageLabel || '—'}</TableCell>
                  <TableCell>
                    {row.nextAction ? (
                      <Button
                        size="small"
                        variant="outlined"
                        data-testid={`next-action-${row.paymentId}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          onNextAction(row)
                        }}
                      >
                        {row.nextAction.label}
                      </Button>
                    ) : (
                      <span className="text-xs text-text-secondary">—</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
                      <NotesIcon className="w-4 h-4 text-gray-500" width={16} height={16} />
                      {row.notesCount || 0}
                    </span>
                  </TableCell>
                </TableRow>
              )
            })}
            {!showSkeleton && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={13}>
                  <p className="text-sm text-text-secondary text-center py-8">
                    {(totalRows ?? rows.length) === 0
                      ? 'No processed payments for the selected date range.'
                      : 'No processed payments match the current filters.'}
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default PaymentsHistoryTable
